export default class Access {
  constructor(target, user) {
    this.user = user;
    switch (target['@type']) {
      case 'Project':
        this.project = target;
        break;
      case 'Task':
        this.task = target;
        this.project = target.project;
        break;
      default:
        throw new Error(`Unhandled entity type "${target['@type']}"`);
    }
  }

  findProjectMember(role = null) {
    const members = this.project?.projectMembers ?? [];
    return members.find((pm) => pm.user.id === this.user.id && (role === null || pm.role === role));
  }

  isProjectAuthor() {
    return this.project?.author?.id === this.user.id;
  }

  isProjectAdmin() {
    return !!this.findProjectMember('admin');
  }

  isProjectWorker() {
    return !!this.findProjectMember('work');
  }

  isProjectStandardMember() {
    return !!this.findProjectMember('standard');
  }

  isProjectMember() {
    return !!this.findProjectMember();
  }

  isTaskAuthor() {
    return this.task?.author?.id === this.user.id;
  }

  isTaskManager() {
    return this.task?.manager?.id === this.user.id;
  }

  isTaskMember() {
    const members = this.task?.members ?? [];
    return members.includes(this.user['@id']);
  }

  isReferralSampleViewAccessible() {
    return this.user.tariff.pricePerDay > 0
      && this.user.payments
        .filter(p => p.status === 'confirmed')
        .reduce((acc, val) => acc + val.amount, 0)
      >= 75000;
  }

  isAccountBlocked() {
    return this.project.author.accountBlocked;
  }

  isProjectArchived() {
    return this.project.isArchived;
  }

  isProjectEditable() {
    return !this.isAccountBlocked() && !this.isProjectArchived();
  }

  checkProjectExists() {
    if (!this.project) {
      throw new Error('Access error: project not found');
    }
  }

  checkTaskExists() {
    if (!this.task) {
      throw new Error('Access error: task not found');
    }
  }

  checkUserExists() {
    if (!this.user) {
      throw new Error('Access error: user not found');
    }
  }

  canCreateReferralSample() {
    return this.isProjectAuthor() && this.user?.tariff?.pricePerDay > 0;
  }

  canEditProject() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor() || this.isProjectAdmin();
  }

  canCopyProject() {
    this.checkProjectExists();
    this.checkUserExists();

    return this.isProjectAuthor()
      || this.isProjectAdmin()
      || (this.project.isReferralSample && this.isReferralSampleViewAccessible());
  }

  canArchiveProject() {
    this.checkProjectExists();
    this.checkUserExists();

    return this.isProjectAuthor();
  }

  canUnarchiveProject() {
    this.checkProjectExists();
    this.checkUserExists();

    if (this.project.author.accountBlocked) {
      return false;
    }

    return this.isProjectAuthor();
  }

  canEditStages() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor() || this.isProjectAdmin() || this.isProjectWorker();
  }

  canCreateTask() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor() || this.isProjectAdmin() || this.isProjectMember();
  }

  canUpdateTask(fullAccess = false) {
    this.checkProjectExists();
    this.checkTaskExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    let hasAccess = this.isProjectAuthor()
      || this.isProjectAdmin()
      || this.isTaskAuthor();

    if (!fullAccess) {
      hasAccess ||= this.isTaskManager();
    }
    return hasAccess;
  }
}
